import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { messagesService, ExecutiveMessage } from '../../services/messages.service';
import { policiesService, Policy } from '../../services/policies.service';
import { Post } from '../../types';
import { isCoAdminOrAdmin } from '../../utils/roles';
import { usePosts } from '../../context/PostsContext';
import { leadsService } from '../../services/leads.service';
import MessagesCard from './MessagesCard';
import PoliciesList from './PoliciesList';
import './RightSidebar.css';

/** Toyota Institute LMS (external) */
const TOYOTA_LMS_TRAINING_URL = 'https://toyotalanka.lmstrends.com/';
/** Served from `frontend/public/corporate-holiday-calendar-2026.pdf` */
const HOLIDAY_CALENDAR_PDF_URL = '/corporate-holiday-calendar-2026.pdf';

function openInNewTab(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function isSpotlightPost(p: Post | null): boolean {
  if (!p) return false;
  return (
    p.category === 'SPOTLIGHT_EOM' ||
    p.category === 'SPOTLIGHT_NEW_EMPLOYEE' ||
    (p.category === 'ANNOUNCEMENT' && !!p.isNewEmployee)
  );
}

function spotlightHasContent(p: Post | null): boolean {
  if (!p || !isSpotlightPost(p)) return false;
  return !!(
    (p.caption && p.caption.trim()) ||
    (p.employeeName && p.employeeName.trim()) ||
    (p.content && p.content.trim()) ||
    (p.cdnLink && p.cdnLink.trim())
  );
}

function spotlightLabel(p: Post): string {
  if (p.category === 'SPOTLIGHT_NEW_EMPLOYEE') return 'New Employee';
  return 'Employee of the Month';
}

const RightSidebar: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { posts } = usePosts();

  // Featured post (Employee of Month card)
  const [featuredPost, setFeaturedPost] = useState<Post | null>(null);
  const [, setFeaturedImage] = useState<string | null>(null);

  // Messages (admin)
  const [messages, setMessages] = useState<ExecutiveMessage[]>([]);
  const [editingType, setEditingType] = useState<'CHAIRMAN' | 'MD' | null>(null);
  const [viewingType, setViewingType] = useState<'CHAIRMAN' | 'MD' | null>(null);
  const [editContent, setEditContent] = useState('');
  const [messageError, setMessageError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);

  // Policies (admin)
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [policyFormData, setPolicyFormData] = useState({
    title: '', content: '', type: 'PRIVACY_POLICY', requireAcceptance: true, isActive: true,
  });
  const [isSavingPolicy, setIsSavingPolicy] = useState(false);
  const [policyError, setPolicyError] = useState('');
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [isUploadingPolicyDocument, setIsUploadingPolicyDocument] = useState(false);
  const [showDeletePolicyModal, setShowDeletePolicyModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<Policy | null>(null);
  const [isDeletingPolicy, setIsDeletingPolicy] = useState(false);

  const canEdit = isCoAdminOrAdmin(user?.role);

  const [showFuelLeadModal, setShowFuelLeadModal] = useState(false);
  const [fuelLeadForm, setFuelLeadForm] = useState({
    customerName: '',
    customerPhone: '',
    suggestedBranch: '',
    suggestedModel: '',
    otherComments: '',
  });
  const [fuelLeadSubmitting, setFuelLeadSubmitting] = useState(false);
  const [fuelLeadError, setFuelLeadError] = useState('');

  const openFuelLeadModal = () => {
    setFuelLeadError('');
    setShowFuelLeadModal(true);
  };

  const closeFuelLeadModal = () => {
    if (fuelLeadSubmitting) return;
    setShowFuelLeadModal(false);
  };

  const handleFuelLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFuelLeadError('');
    setFuelLeadSubmitting(true);
    try {
      await leadsService.submit({
        customerName: fuelLeadForm.customerName.trim(),
        customerPhone: fuelLeadForm.customerPhone.trim(),
        suggestedBranch: fuelLeadForm.suggestedBranch.trim(),
        suggestedModel: fuelLeadForm.suggestedModel.trim(),
        otherComments: fuelLeadForm.otherComments.trim() || undefined,
      });
      setFuelLeadForm({
        customerName: '',
        customerPhone: '',
        suggestedBranch: '',
        suggestedModel: '',
        otherComments: '',
      });
      setShowFuelLeadModal(false);
      alert('Thank you — your lead has been submitted.');
    } catch (err: any) {
      const m = err.response?.data?.message;
      const msg = Array.isArray(m) ? m.join(' ') : m;
      setFuelLeadError(msg || err.message || 'Failed to submit. Please try again.');
    } finally {
      setFuelLeadSubmitting(false);
    }
  };

  useEffect(() => {
    loadMessages();
    if (canEdit) loadPolicies();
  }, [canEdit]);

  /** Keep home/feed from scrolling behind executive message view & edit modals */
  useEffect(() => {
    const open = !!(editingType || viewingType);
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [editingType, viewingType]);

  useEffect(() => {
    const spotlightPosts = posts
      .filter(
        (p) =>
          p.category === 'SPOTLIGHT_EOM' ||
          p.category === 'SPOTLIGHT_NEW_EMPLOYEE' ||
          (p.category === 'ANNOUNCEMENT' && p.isNewEmployee),
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (spotlightPosts.length > 0) {
      setFeaturedPost(spotlightPosts[0]);
      setFeaturedImage(spotlightPosts[0].cdnLink || null);
    } else {
      setFeaturedPost(null);
      setFeaturedImage(null);
    }
  }, [posts]);

  useEffect(() => {
    if (!editingType) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [editingType]);

  useEffect(() => {
    if (!showPolicyModal) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showPolicyModal]);

  // Featured post is derived from PostsContext so it updates in real-time

  // ── Messages ──────────────────────────────────────────────────────────────

  const loadMessages = async () => {
    try {
      const data = await messagesService.getAll();
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleEdit = (type: 'CHAIRMAN' | 'MD') => {
    const message = messages.find((m) => m.type === type);
    setEditContent(message?.content || '');
    setMessageError('');
    setProfilePictureFile(null);
    setEditingType(type);
  };

  const handleSave = async () => {
    if (!editingType) return;
    const trimmed = editContent.trim();
    if (!trimmed) {
      setMessageError('Please enter some message text (not only spaces).');
      return;
    }
    setIsSaving(true);
    setMessageError('');
    try {
      await messagesService.update(editingType, trimmed);
      if (profilePictureFile) {
        setIsUploadingPicture(true);
        try {
          await messagesService.uploadProfilePicture(editingType, profilePictureFile);
        } finally {
          setIsUploadingPicture(false);
        }
      }
      await loadMessages();
      setEditingType(null);
      setEditContent('');
      setProfilePictureFile(null);
    } catch (error: any) {
      const m = error?.response?.data?.message;
      const msg = Array.isArray(m) ? m.join(' ') : m || error?.message || 'Failed to save message';
      setMessageError(msg);
      console.error('Failed to save message:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingType(null);
    setEditContent('');
    setMessageError('');
    setProfilePictureFile(null);
  };

  const getMessageProfilePicture = (type: 'CHAIRMAN' | 'MD') =>
    messages.find((m) => m.type === type)?.profilePicture;

  const getMessageContent = (type: 'CHAIRMAN' | 'MD') =>
    messages.find((m) => m.type === type)?.content || 'No message set';

  // ── Policies ──────────────────────────────────────────────────────────────

  const loadPolicies = async () => {
    try {
      const data = await policiesService.getAll();
      setPolicies(data);
    } catch (error) {
      console.error('Failed to load policies:', error);
    }
  };

  const handleCreatePolicy = () => {
    setEditingPolicy(null);
    setPolicyFormData({ title: '', content: '', type: 'PRIVACY_POLICY', requireAcceptance: true, isActive: true });
    setPolicyError('');
    setPolicyFile(null);
    setShowPolicyModal(true);
  };

  const handleEditPolicy = (policy: Policy) => {
    setEditingPolicy(policy);
    setPolicyFormData({ title: policy.title, content: policy.content, type: policy.type, requireAcceptance: policy.requireAcceptance, isActive: policy.isActive });
    setPolicyError('');
    setPolicyFile(null);
    setShowPolicyModal(true);
  };

  const handlePolicySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPolicyError('');
    if (!policyFile && !policyFormData.content && !editingPolicy?.documentUrl) {
      setPolicyError('Please provide policy content or upload a document');
      return;
    }
    setIsSavingPolicy(true);
    try {
      let documentUrl = editingPolicy?.documentUrl || '';
      if (policyFile) {
        setIsUploadingPolicyDocument(true);
        try {
          const r = await policiesService.uploadDocument(policyFile);
          documentUrl = r.url;
        } finally {
          setIsUploadingPolicyDocument(false);
        }
      }
      const payload: any = { title: policyFormData.title, content: policyFormData.content, type: policyFormData.type, requireAcceptance: policyFormData.requireAcceptance };
      if (documentUrl) payload.documentUrl = documentUrl;
      if (editingPolicy) {
        await policiesService.update(editingPolicy.id, payload);
      } else {
        await policiesService.create(payload);
      }
      setShowPolicyModal(false);
      loadPolicies();
    } catch (error: any) {
      setPolicyError(error.response?.data?.message || 'Failed to save policy');
    } finally {
      setIsSavingPolicy(false);
    }
  };

  const handleDeletePolicy = (policy: Policy) => {
    setPolicyToDelete(policy);
    setShowDeletePolicyModal(true);
  };

  const confirmDeletePolicy = async () => {
    if (!policyToDelete) return;
    setIsDeletingPolicy(true);
    try {
      await policiesService.delete(policyToDelete.id);
      setShowDeletePolicyModal(false);
      setPolicyToDelete(null);
      loadPolicies();
    } catch {
      alert('Failed to delete policy.');
    } finally {
      setIsDeletingPolicy(false);
    }
  };

  const cancelDeletePolicy = () => {
    setShowDeletePolicyModal(false);
    setPolicyToDelete(null);
  };

  const handleTogglePolicyStatus = async (policy: Policy) => {
    try {
      await policiesService.update(policy.id, { isActive: !policy.isActive });
      loadPolicies();
    } catch (error) {
      console.error('Failed to toggle policy status:', error);
    }
  };

  const activePolicies = policies.filter((p) => p.isActive);

  return (
    <aside className="right-sidebar">

      {/* ── Vehicle Leads (interactive hero card) ── */}
      <div
        className="sidebar-card rs-fuel-lead-card"
        role="button"
        tabIndex={0}
        aria-label="Vehicle Leads — open referral form"
        onClick={openFuelLeadModal}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFuelLeadModal();
          }
        }}
      >
        <div className="rs-fuel-lead-visual" aria-hidden>
          <img
            className="rs-fuel-lead-img"
            src="/fuel-a-lead-hero.png"
            alt=""
            loading="lazy"
            decoding="async"
          />
          <div className="rs-fuel-lead-overlay" />
          <div className="rs-fuel-lead-shine" />
          <div className="rs-fuel-lead-float-icons">
            <span className="rs-fuel-float">🚙</span>
            <span className="rs-fuel-float">🚗</span>
            <span className="rs-fuel-float">🏁</span>
          </div>
          <span className="rs-fuel-lead-badge">Sales referral</span>
        </div>
        <div className="rs-fuel-lead-body">
          <div className="rs-fuel-lead-title-row">
            <span className="rs-fuel-lead-pump" aria-hidden>
              ⛽
            </span>
            <h4 className="rs-fuel-lead-title">Vehicle Leads</h4>
          </div>
          <p className="rs-fuel-lead-subtitle">
            Tap to refer a customer — branch, model & contact details in one quick form.
          </p>
          <div className="rs-fuel-lead-cta">
            <span className="rs-fuel-lead-cta-text">Fuel a Lead</span>
            <span className="rs-fuel-lead-cta-arrow">→</span>
          </div>
        </div>
      </div>

      {/* ── Sidebar spotlight (Employee of the Month / New employee) ── */}
      {featuredPost && spotlightHasContent(featuredPost) && (
        <div className="rs-eom-card">
          <div className="rs-eom-header">
            <span>{spotlightLabel(featuredPost)}</span>
            <span className="rs-eom-header-emojis">👩‍🦰 🎉</span>
          </div>
          <div className="rs-eom-body">
            {featuredPost.employeeName && (
              <div className="rs-eom-employee-name">{featuredPost.employeeName}</div>
            )}
            {featuredPost.employeeDepartment && (
              <div className="rs-eom-employee-dept">{featuredPost.employeeDepartment}</div>
            )}
            <h2 className="rs-eom-title">{featuredPost.caption}</h2>
            {featuredPost.content && (
              <p className="rs-eom-content">
                {featuredPost.content.slice(0, 180)}
                {featuredPost.content.length > 180 ? '...' : ''}
              </p>
            )}
            <div className="rs-eom-cta">
              {featuredPost.category === 'SPOTLIGHT_NEW_EMPLOYEE' ? (
                <>
                  <span>Welcome to the team</span>
                  <span className="rs-eom-cta-target"> 🎉 </span>
                  <span>We&apos;re glad you&apos;re here</span>
                </>
              ) : (
                <>
                  <span>Get Ready to</span>
                  <span className="rs-eom-cta-target"> 🎯 </span>
                  <span>Celebrate Excellence</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Quick Links ── */}
      <div className="sidebar-card rs-quicklinks-card">
        <div className="rs-ql-header">
          <span className="ls-section-bar" style={{ background: 'var(--primary)' }} />
          <h4 className="rs-ql-title">Quick Links</h4>
        </div>
        <div className="rs-ql-grid">
          <button className="rs-ql-item" onClick={() => navigate('/documents')}>
            <div className="rs-ql-icon rs-ql-icon-docs">📄</div>
            <span className="rs-ql-label">Documents</span>
          </button>
          <button
            type="button"
            className="rs-ql-item"
            onClick={() => openInNewTab(TOYOTA_LMS_TRAINING_URL)}
            title="Toyota Institute LMS"
          >
            <div className="rs-ql-icon rs-ql-icon-train">🎓</div>
            <span className="rs-ql-label">Training</span>
          </button>
          <button
            type="button"
            className="rs-ql-item"
            onClick={() => openInNewTab(HOLIDAY_CALENDAR_PDF_URL)}
            title="Corporate Holiday Calendar 2026"
          >
            <div className="rs-ql-icon rs-ql-icon-cal">📅</div>
            <span className="rs-ql-label">Holiday</span>
          </button>
        </div>
      </div>

      {/* ── Policies (Admin only) ── */}
      {canEdit && (
        <PoliciesList
          policies={activePolicies}
          canEdit={canEdit}
          onCreate={handleCreatePolicy}
          onEdit={handleEditPolicy}
          onToggle={handleTogglePolicyStatus}
          onDelete={(id) => {
            const p = policies.find((x) => x.id === id);
            if (p) handleDeletePolicy(p);
          }}
        />
      )}

      {/* ── Executive Messages ── */}
      <MessagesCard
        messages={messages}
        canEdit={canEdit}
        onEdit={handleEdit}
        onView={(t) => setViewingType(t)}
        getProfilePicture={getMessageProfilePicture}
        getContent={getMessageContent}
      />

      {/* ── View Message Modal ── */}
      {viewingType && createPortal(
        <div className="modal-overlay message-modal-overlay" 
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) setViewingType(null);
        }}>
          <div className="modal-content view-message-modal" 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Message From {viewingType === 'CHAIRMAN' ? 'Chairman' : 'MD'}</h2>
              <button className="modal-close" onClick={() => setViewingType(null)}>×</button>
            </div>
            <div className="modal-body view-message-body">
              {getMessageProfilePicture(viewingType) && (
                <div className="message-profile-picture-large">
                  <img src={getMessageProfilePicture(viewingType)} alt={viewingType} />
                </div>
              )}
              <div className="message-content-full"><p>{getMessageContent(viewingType)}</p></div>
            </div>
            <div className="modal-footer">
              {canEdit && (
                <button className="btn-submit" onClick={() => { setViewingType(null); handleEdit(viewingType); }}>Edit Message</button>
              )}
              <button className="btn-cancel" onClick={() => setViewingType(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Edit Message Modal (portal + scroll contained — see CSS .message-edit-modal) ── */}
      {editingType &&
        createPortal(
          <div className="modal-overlay message-modal-overlay" 
          onMouseDown={(e) => {
            // Only close if the mousedown originated on the overlay itself
            if (e.target === e.currentTarget) {
              handleCancel();
            }
          }}
          >
            <div
              className="modal-content message-modal message-edit-modal"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="message-edit-title"
            >
              <div className="modal-header">
                <h2 id="message-edit-title">
                  Edit {editingType === 'CHAIRMAN' ? 'Chairman' : 'MD'} Message
                </h2>
                <button type="button" className="modal-close" onClick={handleCancel}>
                  ×
                </button>
              </div>
              <div className="modal-body message-edit-modal-body">
                {messageError && <div className="error-message">{messageError}</div>}
                <div className="form-group">
                  <label>Profile Picture</label>
                  {getMessageProfilePicture(editingType) && !profilePictureFile && (
                    <div className="current-profile-picture">
                      <img src={getMessageProfilePicture(editingType)} alt="Current" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setProfilePictureFile(e.target.files?.[0] || null)}
                    disabled={isUploadingPicture}
                  />
                  {profilePictureFile && (
                    <div className="preview-profile-picture">
                      <img src={URL.createObjectURL(profilePictureFile)} alt="Preview" />
                      <span>New photo selected</span>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>
                    Message Content
                    <span className="char-counter">{editContent.length.toLocaleString()} characters</span>
                  </label>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    placeholder="Enter message..."
                    rows={10}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-submit"
                  onClick={handleSave}
                  disabled={isSaving || isUploadingPicture}
                >
                  {isSaving || isUploadingPicture ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ── Policy Modal ── */}
      {showPolicyModal &&
        createPortal(
          <div className="rs-modal-overlay" onClick={() => setShowPolicyModal(false)}>
            <div className="modal-content policy-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingPolicy ? 'Edit Policy' : 'Add Policy'}</h2>
                <button className="modal-close" onClick={() => setShowPolicyModal(false)}>×</button>
              </div>
              <form onSubmit={handlePolicySubmit}>
                <div className="modal-body">
                  {policyError && <div className="error-message">{policyError}</div>}
                  <div className="form-group">
                    <label>Title *</label>
                    <input type="text" value={policyFormData.title} onChange={(e) => setPolicyFormData({ ...policyFormData, title: e.target.value })} placeholder="e.g., Privacy Policy" required />
                  </div>
                  <div className="form-group">
                    <label>Type *</label>
                    <select value={policyFormData.type} onChange={(e) => setPolicyFormData({ ...policyFormData, type: e.target.value })} required>
                      <option value="PRIVACY_POLICY">Privacy Policy</option>
                      <option value="TERMS_OF_SERVICE">Terms of Service</option>
                      <option value="REGULATION">Regulation</option>
                      <option value="CODE_OF_CONDUCT">Code of Conduct</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Document (optional)</label>
                    {editingPolicy?.documentUrl && !policyFile && (
                      <div className="current-policy-document">
                        <a href={editingPolicy.documentUrl} target="_blank" rel="noopener noreferrer">Current document</a>
                      </div>
                    )}
                    <input type="file" onChange={(e) => setPolicyFile(e.target.files?.[0] || null)} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" />
                    <span className="field-note">Supported: PDF, Word, Excel, PowerPoint</span>
                  </div>
                  <div className="form-group">
                    <label>Content</label>
                    <textarea value={policyFormData.content} onChange={(e) => setPolicyFormData({ ...policyFormData, content: e.target.value })} placeholder="Enter policy content..." rows={8} required={!policyFile && !editingPolicy?.documentUrl} />
                  </div>
                  {editingPolicy && (
                    <div className="form-group checkbox-group">
                      <label>
                        <input type="checkbox" checked={policyFormData.isActive} onChange={(e) => setPolicyFormData({ ...policyFormData, isActive: e.target.checked })} />
                        <span>Active</span>
                      </label>
                    </div>
                  )}
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={policyFormData.requireAcceptance} onChange={(e) => setPolicyFormData({ ...policyFormData, requireAcceptance: e.target.checked })} />
                      <span>Require Acceptance</span>
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowPolicyModal(false)} disabled={isSavingPolicy || isUploadingPolicyDocument}>Cancel</button>
                  <button type="submit" className="btn-submit" disabled={isSavingPolicy || isUploadingPolicyDocument}>{isSavingPolicy || isUploadingPolicyDocument ? 'Saving...' : 'Save'}</button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {/* ── Vehicle Leads Modal ── */}
      {showFuelLeadModal &&
        createPortal(
          <div className="modal-overlay" onClick={closeFuelLeadModal}>
            <div className="modal-content fuel-lead-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Vehicle Leads</h2>
                <button type="button" className="modal-close" onClick={closeFuelLeadModal} aria-label="Close">
                  ×
                </button>
              </div>
              <form onSubmit={handleFuelLeadSubmit}>
                <div className="modal-body">
                  {fuelLeadError && <div className="error-message">{fuelLeadError}</div>}
                  <div className="form-group">
                    <label htmlFor="fuel-customer-name">Customer name *</label>
                    <input
                      id="fuel-customer-name"
                      type="text"
                      value={fuelLeadForm.customerName}
                      onChange={(e) => setFuelLeadForm({ ...fuelLeadForm, customerName: e.target.value })}
                      placeholder="e.g. John Perera"
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="fuel-customer-phone">Customer phone number *</label>
                    <input
                      id="fuel-customer-phone"
                      type="tel"
                      value={fuelLeadForm.customerPhone}
                      onChange={(e) => setFuelLeadForm({ ...fuelLeadForm, customerPhone: e.target.value })}
                      placeholder="e.g. 077 123 4567"
                      required
                      autoComplete="tel"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="fuel-branch">Suggested branch to the customer *</label>
                    <input
                      id="fuel-branch"
                      type="text"
                      value={fuelLeadForm.suggestedBranch}
                      onChange={(e) => setFuelLeadForm({ ...fuelLeadForm, suggestedBranch: e.target.value })}
                      placeholder="e.g. Colombo 3, Kandy"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="fuel-model">Suggested model *</label>
                    <input
                      id="fuel-model"
                      type="text"
                      value={fuelLeadForm.suggestedModel}
                      onChange={(e) => setFuelLeadForm({ ...fuelLeadForm, suggestedModel: e.target.value })}
                      placeholder="e.g. Corolla Cross, Hilux"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="fuel-comments">Other specifications or comments</label>
                    <textarea
                      id="fuel-comments"
                      value={fuelLeadForm.otherComments}
                      onChange={(e) => setFuelLeadForm({ ...fuelLeadForm, otherComments: e.target.value })}
                      placeholder="Optional details, trim, budget, timing…"
                      rows={4}
                      style={{ resize: 'vertical' }}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeFuelLeadModal} disabled={fuelLeadSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={fuelLeadSubmitting}>
                    {fuelLeadSubmitting ? 'Submitting…' : 'Submit lead'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {/* ── Delete Policy Modal ── */}
      {showDeletePolicyModal && createPortal(
        <div className="modal-overlay" onClick={cancelDeletePolicy}>
          <div className="modal-content delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Policy</h2>
              <button className="modal-close" onClick={cancelDeletePolicy}>×</button>
            </div>
            <div className="modal-body">
              <p className="delete-confirmation-message">
                Are you sure you want to delete <strong>"{policyToDelete?.title}"</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDeletePolicy} disabled={isDeletingPolicy}>Cancel</button>
              <button type="button" className="btn-delete" onClick={confirmDeletePolicy} disabled={isDeletingPolicy}>{isDeletingPolicy ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </aside>
  );
};

export default RightSidebar;
